import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLandingPageSite } from '../hooks/useLandingPageSite';
import { RenderedWebsite } from './RenderedWebsite';

interface LandingPageRouteProps {
  /**
   * Slug to resolve against the designated landing site. If omitted, derived
   * from the current location (for the catch-all route).
   */
  slug?: string;
  /**
   * Element to render when no landing site is designated, the landing site
   * has no page at this slug, or the fetch fails. Typically a static fallback
   * like <Welcome />, <Pricing />, <Contact />, or <Navigate to="/" />.
   */
  fallback: React.ReactElement;
  /**
   * Element to render while landing or page data is loading. Defaults to
   * `fallback` so routes like `/pricing` can show a static page immediately.
   * Pass `null` on catch-all routes so navigation is not aborted before the
   * page fetch completes (e.g. avoid redirecting home while loading).
   */
  loadingFallback?: React.ReactElement | null;
}

export const LandingPageRoute: React.FC<LandingPageRouteProps> = ({
  slug,
  fallback,
  loadingFallback = fallback,
}) => {
  const location = useLocation();
  const resolvedSlug = slug ?? location.pathname.replace(/^\//, '');
  const data = useLandingPageSite(resolvedSlug);

  if (data.loading) {
    return loadingFallback ?? null;
  }
  if (data.notConfigured) return fallback;
  if (data.error) return fallback;
  if (data.pageNotFound) return fallback;
  if (!data.website || !data.page) return fallback;

  return (
    <RenderedWebsite
      website={data.website}
      page={data.page}
      components={data.components}
      navigation={data.navigation}
      theme={data.theme}
      layout={data.layout}
      showBranding={data.showBranding}
      identifier={data.website.subdomain || ''}
    />
  );
};
